import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { House, Airplane, MapPin, Clock } from "@phosphor-icons/react";

function formatDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso + "T00:00:00");
        return d.toLocaleDateString("it-IT", {
            weekday: "short",
            day: "numeric",
            month: "short",
        });
    } catch {
        return iso;
    }
}

export default function Matches() {
    const [matches, setMatches] = useState([]);
    const [filter, setFilter] = useState("upcoming");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/matches");
                setMatches(data);
            } catch (e) {
                if (process.env.NODE_ENV !== "production") {
                    console.warn("matches load failed:", e);
                }
            }
        })();
    }, []);

    const today = new Date().toISOString().slice(0, 10);
    const filtered = useMemo(() => {
        const list = [...matches];
        if (filter === "upcoming")
            return list
                .filter((m) => m.date >= today)
                .sort((a, b) => a.date.localeCompare(b.date));
        if (filter === "past")
            return list
                .filter((m) => m.date < today)
                .sort((a, b) => b.date.localeCompare(a.date));
        return list.sort((a, b) => a.date.localeCompare(b.date));
    }, [matches, filter, today]);

    return (
        <div className="space-y-6" data-testid="matches-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Calendario
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
                        Partite
                    </h1>
                </div>
            </div>

            <div className="flex gap-1 rounded-xl hairline bg-card p-1 w-fit">
                {[
                    { k: "upcoming", label: "Prossime" },
                    { k: "past", label: "Passate" },
                    { k: "all", label: "Tutte" },
                ].map((t) => (
                    <button
                        key={t.k}
                        data-testid={`matches-filter-${t.k}`}
                        onClick={() => setFilter(t.k)}
                        className={`px-4 py-2 rounded-lg text-xs uppercase tracking-widest font-semibold press ${
                            filter === t.k
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:text-foreground"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="space-y-3">
                {filtered.map((m) => (
                    <Link
                        key={m.id}
                        to={`/partite/${m.id}`}
                        data-testid={`match-row-${m.id}`}
                        className="block rounded-2xl hairline bg-card p-4 md:p-5 press hover:border-primary/40"
                    >
                        <div className="flex items-center gap-4">
                            <div className="w-16 shrink-0">
                                <div className="num text-3xl md:text-4xl font-black leading-none">
                                    {new Date(m.date + "T00:00:00").getDate()}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">
                                    {formatDate(m.date)}
                                </div>
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full bg-primary/15 text-primary flex items-center gap-1">
                                        {m.home ? (
                                            <House size={11} weight="fill" />
                                        ) : (
                                            <Airplane size={11} weight="fill" />
                                        )}
                                        {m.home ? "Casa" : "Trasferta"}
                                    </span>
                                    <span className="num text-sm font-bold">
                                        {m.kickoff_time}
                                    </span>
                                </div>
                                <div className="font-display text-lg md:text-xl font-bold uppercase tracking-tight mt-1 truncate">
                                    vs {m.opponent}
                                    {m.is_finished && (
                                        <span
                                            data-testid={`score-${m.id}`}
                                            className="ml-2 num text-primary"
                                        >
                                            {m.home ? m.home_score : m.away_score}-
                                            {m.home ? m.away_score : m.home_score}
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 mt-1">
                                    <span className="flex items-center gap-1">
                                        <MapPin size={12} weight="duotone" />
                                        {m.field_name || "Campo da definire"}
                                    </span>
                                    {m.meeting_time && (
                                        <span className="flex items-center gap-1">
                                            <Clock size={12} weight="duotone" />
                                            Ritrovo {m.meeting_time}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Link>
                ))}
                {filtered.length === 0 && (
                    <div className="rounded-2xl hairline p-8 text-center bg-card bg-field">
                        <div className="text-muted-foreground">Nessuna partita.</div>
                    </div>
                )}
            </div>
        </div>
    );
}
